import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Car, RefreshCw, Eye, Clock, AlertCircle, CheckCircle, UserX, FileText,
  Paperclip, Send, Printer, User, Building2, Euro, Calendar as CalIcon,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATI = [
  { value: "ricevuta", label: "Ricevuta", icon: AlertCircle, color: "bg-amber-50 text-amber-700 border-amber-200" },
  { value: "in_verifica_documenti", label: "Verifica documenti", icon: FileText, color: "bg-blue-50 text-blue-700 border-blue-200" },
  { value: "documenti_ok", label: "Documenti OK", icon: CheckCircle, color: "bg-teal-50 text-teal-700 border-teal-200" },
  { value: "inviata_partner", label: "Inviata a partner", icon: Send, color: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  { value: "approvata", label: "Approvata", icon: CheckCircle, color: "bg-green-50 text-green-700 border-green-200" },
  { value: "rifiutata", label: "Rifiutata", icon: UserX, color: "bg-red-50 text-red-700 border-red-200" },
  { value: "contratto_firmato", label: "Contratto firmato", icon: CheckCircle, color: "bg-purple-50 text-purple-700 border-purple-200" },
  { value: "consegnata", label: "Consegnata", icon: Car, color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { value: "archiviata", label: "Archiviata", icon: UserX, color: "bg-zinc-50 text-zinc-500 border-zinc-200" },
];
const CONF = (v) => STATI.find(s => s.value === v) || STATI[0];

export default function PraticheNoleggioTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [refreshingCatalog, setRefreshingCatalog] = useState(false);

  const refreshCatalog = async () => {
    setRefreshingCatalog(true);
    try {
      const { data } = await axios.post(`${API}/admin/nlt/refresh-now`, {}, { withCredentials: true });
      const a = data?.arval?.count ?? "?";
      const y = data?.ayvens?.count ?? "?";
      toast.success(`Catalogo aggiornato: Arval ${a} + Ayvens ${y} veicoli`);
    } catch {
      toast.error("Errore aggiornamento catalogo NLT");
    }
    setRefreshingCatalog(false);
  };

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/noleggio/pratiche`, {
        withCredentials: true,
        params: filter ? { stato: filter } : {},
      });
      setItems(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openDetail = async (it) => {
    try {
      const { data } = await axios.get(`${API}/noleggio/pratiche/${it.id}`, { withCredentials: true });
      setSelected(data);
      setOpen(true);
    } catch (err) { toast.error("Errore"); }
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "-";
  const fmtEur = (v) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v || 0);

  if (loading) return <div className="bg-white border rounded-xl p-8 text-center text-zinc-400">Caricamento...</div>;

  const stats = STATI.reduce((acc, s) => { acc[s.value] = items.filter(i => i.stato === s.value).length; return acc; }, {});

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
            <Car className="w-5 h-5 text-[#163321]" /> Pratiche Noleggio
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5">Gestione pipeline richieste NLT — clicca per entrare nella pratica</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={(v) => setFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-40 h-9 text-xs rounded-lg"><SelectValue placeholder="Tutti gli stati" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              {STATI.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchItems} className="h-9" title="Aggiorna pratiche"><RefreshCw className="w-3.5 h-3.5" /></Button>
          <Button variant="outline" size="sm" onClick={refreshCatalog} disabled={refreshingCatalog} className="h-9 text-xs gap-1.5 border-[#163321]/20 text-[#163321] hover:bg-[#163321]/5" title="Aggiorna subito il catalogo NLT (auto ogni ora + mezzanotte)" data-testid="refresh-nlt-catalog-btn">
            <Car className="w-3.5 h-3.5" />
            {refreshingCatalog ? "Aggiorno..." : "Aggiorna Catalogo NLT"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        <div className="bg-white rounded-xl border p-3"><p className="text-[10px] text-zinc-500">Totali</p><p className="font-heading text-lg font-bold">{items.length}</p></div>
        <div className="bg-white rounded-xl border p-3"><p className="text-[10px] text-zinc-500">Da lavorare</p><p className="font-heading text-lg font-bold text-amber-600">{stats.ricevuta || 0}</p></div>
        <div className="bg-white rounded-xl border p-3"><p className="text-[10px] text-zinc-500">In corso</p><p className="font-heading text-lg font-bold text-blue-600">{(stats.in_verifica_documenti||0)+(stats.documenti_ok||0)+(stats.inviata_partner||0)}</p></div>
        <div className="bg-white rounded-xl border p-3"><p className="text-[10px] text-zinc-500">Consegnate</p><p className="font-heading text-lg font-bold text-emerald-600">{stats.consegnata || 0}</p></div>
      </div>

      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50/50 border-b">
              <tr>
                <th className="text-left px-4 py-3 text-[10px] font-medium text-zinc-500 uppercase">Pratica</th>
                <th className="text-left px-4 py-3 text-[10px] font-medium text-zinc-500 uppercase">Cliente</th>
                <th className="text-left px-4 py-3 text-[10px] font-medium text-zinc-500 uppercase">Auto</th>
                <th className="text-left px-4 py-3 text-[10px] font-medium text-zinc-500 uppercase">Canone</th>
                <th className="text-left px-4 py-3 text-[10px] font-medium text-zinc-500 uppercase">Ricevuta</th>
                <th className="text-left px-4 py-3 text-[10px] font-medium text-zinc-500 uppercase">Stato</th>
                <th className="text-left px-4 py-3 text-[10px] font-medium text-zinc-500 uppercase"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {items.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-12 text-zinc-400 text-sm">Nessuna pratica {filter ? `nello stato ${filter}` : "ricevuta"}</td></tr>
              ) : items.map(p => {
                const conf = CONF(p.stato);
                return (
                  <tr key={p.id} className="hover:bg-zinc-50/60 cursor-pointer" onClick={() => openDetail(p)} data-testid={`pratica-row-${p.numero_pratica}`}>
                    <td className="px-4 py-3 font-mono text-[11px] font-semibold text-[#163321]">{p.numero_pratica}</td>
                    <td className="px-4 py-3 text-xs">
                      <p className="font-medium text-zinc-900">{p.contatto?.nome} {p.contatto?.cognome}</p>
                      <p className="text-[10px] text-zinc-500 inline-flex items-center gap-1">
                        {p.tipo_cliente === "privato" ? <User className="w-3 h-3" /> : <Building2 className="w-3 h-3" />}
                        {p.tipo_cliente === "privato" ? "Privato" : p.tipo_cliente === "azienda" ? "Azienda" : "P.IVA"}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <div className="flex items-center gap-1.5">
                        <p className="font-medium">{p.car_info?.brand} {p.car_info?.model}</p>
                        {p.configurazione_personalizzata && (
                          <span className="px-1.5 py-0.5 rounded-full bg-[#D97B58]/10 text-[#D97B58] border border-[#D97B58]/30 text-[9px] font-bold uppercase tracking-wider" title="Quotazione personalizzata richiesta">Custom</span>
                        )}
                      </div>
                      <p className="text-[10px] text-zinc-500">{p.configurazione?.durata_mesi}m · {p.configurazione?.km_annui?.toLocaleString("it-IT")}km</p>
                    </td>
                    <td className="px-4 py-3 text-xs font-semibold text-zinc-900">{fmtEur(p.car_info?.canone_mensile_base)}</td>
                    <td className="px-4 py-3 text-[11px] text-zinc-500">{fmtDate(p.created_at)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${conf.color}`}>
                        <conf.icon className="w-3 h-3" />{conf.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Eye className="w-4 h-4 text-zinc-400" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl" data-testid="pratica-detail-dialog">
          {selected && <PraticaDetail p={selected} onRefresh={async () => { await fetchItems(); const { data } = await axios.get(`${API}/noleggio/pratiche/${selected.id}`, { withCredentials: true }); setSelected(data); }} fmtDate={fmtDate} fmtEur={fmtEur} />}
        </DialogContent>
      </Dialog>
    </>
  );
}

function PraticaDetail({ p, onRefresh, fmtDate, fmtEur }) {
  const [stato, setStato] = useState(p.stato);
  const [partner, setPartner] = useState(p.partner_destinazione || "");
  const [nota, setNota] = useState("");
  const [saving, setSaving] = useState(false);
  const conf = CONF(p.stato);

  const saveStato = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/noleggio/pratiche/${p.id}`, { stato, partner_destinazione: partner }, { withCredentials: true });
      toast.success("Stato aggiornato");
      await onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } finally { setSaving(false); }
  };

  const saveNota = async () => {
    if (!nota.trim()) return toast.error("Nota vuota");
    setSaving(true);
    try {
      await axios.patch(`${API}/noleggio/pratiche/${p.id}`, { nota }, { withCredentials: true });
      toast.success("Nota salvata");
      setNota("");
      await onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } finally { setSaving(false); }
  };

  const stampaPDF = () => window.print();

  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-heading flex items-center gap-2">
          <Car className="w-5 h-5 text-[#163321]" />
          <span className="font-mono text-sm">{p.numero_pratica}</span>
          <span className={`ml-auto inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border ${conf.color}`}>
            <conf.icon className="w-3 h-3" />{conf.label}
          </span>
        </DialogTitle>
      </DialogHeader>

      <div className="space-y-5 mt-3 text-sm">
        {/* Tipologia richiesta */}
        {p.configurazione_personalizzata && (
          <div className="bg-[#D97B58]/10 border border-[#D97B58]/30 rounded-xl px-4 py-2.5 flex items-start gap-2" data-testid="pratica-custom-badge">
            <AlertCircle className="w-4 h-4 text-[#D97B58] shrink-0 mt-0.5" />
            <div className="flex-1 text-xs">
              <p className="font-semibold text-[#0F1F14]">Richiesta di quotazione personalizzata</p>
              <p className="text-zinc-700 mt-0.5">
                Il cliente ha modificato i parametri rispetto all'offerta di listino.
                {p.configurazione?.differenza_km ? ` Differenza km: ${p.configurazione.differenza_km > 0 ? "+" : ""}${p.configurazione.differenza_km.toLocaleString("it-IT")}/anno.` : ""}
                {p.configurazione?.differenza_durata ? ` Differenza durata: ${p.configurazione.differenza_durata > 0 ? "+" : ""}${p.configurazione.differenza_durata} mesi.` : ""}
                {" Preparare offerta su misura."}
              </p>
            </div>
          </div>
        )}

        {/* AUTO */}
        <div className="bg-zinc-50 rounded-xl p-4 flex items-center gap-4">
          {p.car_info?.imageUrl && <img src={p.car_info.imageUrl} alt="" className="w-24 h-16 object-contain" />}
          <div className="flex-1">
            <p className="text-[10px] uppercase text-zinc-500">{p.car_info?.provider} · {p.car_info?.brand}</p>
            <p className="font-heading text-base font-semibold">{p.car_info?.model}</p>
            <p className="text-xs text-zinc-600">
              <strong>Richiesto:</strong> {p.configurazione?.durata_mesi} mesi · {p.configurazione?.km_annui?.toLocaleString("it-IT")} km/anno · Anticipo {fmtEur(p.configurazione?.anticipo)}
            </p>
            {p.configurazione_personalizzata && (p.configurazione?.durata_base || p.configurazione?.km_base) && (
              <p className="text-[10px] text-zinc-500 mt-0.5">
                Listino: {p.configurazione?.durata_base || "—"} mesi · {p.configurazione?.km_base?.toLocaleString("it-IT") || "—"} km/anno
              </p>
            )}
          </div>
          <div className="text-right">
            <p className="text-[10px] text-zinc-500">Canone listino</p>
            <p className="font-heading text-xl font-bold text-[#163321]">{fmtEur(p.car_info?.canone_mensile_base)}</p>
            <p className="text-[10px] text-zinc-500">+IVA/mese</p>
            {p.configurazione_personalizzata && <p className="text-[9px] text-[#D97B58] font-semibold mt-0.5 uppercase tracking-wider">Da ricalcolare</p>}
          </div>
        </div>

        {/* CLIENTE */}
        <div>
          <p className="text-[10px] uppercase text-zinc-500 font-semibold mb-2">Cliente</p>
          <div className="bg-white border rounded-xl p-4 space-y-1 text-xs">
            <p><strong>{p.contatto?.nome} {p.contatto?.cognome}</strong> ({p.tipo_cliente})</p>
            <p><a href={`mailto:${p.contatto?.email}`} className="text-[#163321] underline">{p.contatto?.email}</a> · <a href={`tel:${p.contatto?.telefono}`} className="text-[#163321] underline">{p.contatto?.telefono}</a></p>
            {p.tipo_cliente === "privato" ? (
              <>
                <p>CF: <span className="font-mono">{p.dati_fiscali?.codice_fiscale}</span> · Nato il {p.dati_fiscali?.data_nascita} a {p.dati_fiscali?.luogo_nascita}</p>
                <p>{p.dati_fiscali?.indirizzo}, {p.dati_fiscali?.citta} ({p.dati_fiscali?.provincia}) - {p.dati_fiscali?.cap}</p>
              </>
            ) : (
              <>
                <p><strong>{p.dati_azienda?.ragione_sociale}</strong> · P.IVA {p.dati_azienda?.partita_iva}{p.dati_azienda?.codice_sdi && ` · SDI ${p.dati_azienda.codice_sdi}`}</p>
                {p.dati_azienda?.pec && <p>PEC: {p.dati_azienda.pec}</p>}
                {p.dati_azienda?.settore && <p>Settore: {p.dati_azienda.settore} · Attiva da {p.dati_azienda.anni_attivita} anni</p>}
              </>
            )}
          </div>
        </div>

        {/* REDDITO */}
        {p.tipo_cliente === "privato" && p.dati_reddito && (
          <div>
            <p className="text-[10px] uppercase text-zinc-500 font-semibold mb-2">Situazione economica</p>
            <div className="bg-white border rounded-xl p-4 text-xs grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div><p className="text-zinc-500 text-[10px]">Impiego</p><p className="font-medium">{p.dati_reddito.tipo_impiego}</p></div>
              <div><p className="text-zinc-500 text-[10px]">Datore</p><p className="font-medium">{p.dati_reddito.datore_lavoro || "-"}</p></div>
              <div><p className="text-zinc-500 text-[10px]">Reddito mens.</p><p className="font-medium inline-flex items-center gap-1"><Euro className="w-3 h-3" />{p.dati_reddito.reddito_mensile}</p></div>
              <div><p className="text-zinc-500 text-[10px]">Anni impiego</p><p className="font-medium">{p.dati_reddito.anni_impiego}</p></div>
            </div>
          </div>
        )}

        {/* DOCUMENTI */}
        {p.file_ids?.length > 0 && (
          <div>
            <p className="text-[10px] uppercase text-zinc-500 font-semibold mb-2">Documenti allegati ({p.file_ids.length})</p>
            <div className="flex flex-wrap gap-2">
              {p.file_ids.map(fid => (
                <a key={fid} href={`${API}/files/${fid}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-2 bg-zinc-100 hover:bg-zinc-200 rounded-lg text-xs">
                  <Paperclip className="w-3.5 h-3.5" /> {fid.slice(0, 8)}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* AZIONI */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
          <p className="text-[10px] uppercase text-amber-900 font-bold">Gestione pratica</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
            <div>
              <Label className="text-xs">Stato</Label>
              <Select value={stato} onValueChange={setStato}>
                <SelectTrigger className="h-9 rounded-lg bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATI.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Partner destinazione</Label>
              <Select value={partner || "-"} onValueChange={(v) => setPartner(v === "-" ? "" : v)}>
                <SelectTrigger className="h-9 rounded-lg bg-white"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="-">-</SelectItem>
                  <SelectItem value="Ayvens">Ayvens</SelectItem>
                  <SelectItem value="Arval">Arval</SelectItem>
                  <SelectItem value="LeasePlan">LeasePlan</SelectItem>
                  <SelectItem value="Alphabet">Alphabet</SelectItem>
                  <SelectItem value="Altro">Altro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={saveStato} disabled={saving} className="bg-[#163321] hover:bg-[#1d4a2e] text-white rounded-lg h-9" data-testid="save-stato-pratica">
              Salva
            </Button>
          </div>
          <div>
            <Label className="text-xs">Nota interna</Label>
            <div className="flex gap-2 mt-1">
              <Textarea value={nota} onChange={e => setNota(e.target.value)} rows={2} maxLength={2000} placeholder="Aggiungi una nota..." className="bg-white rounded-lg text-xs flex-1 resize-none" />
              <Button onClick={saveNota} disabled={saving} variant="outline" className="rounded-lg" data-testid="save-nota-pratica">Aggiungi</Button>
            </div>
          </div>
          {p.note_interne?.length > 0 && (
            <div className="space-y-1.5">
              {p.note_interne.slice().reverse().map((n, i) => (
                <div key={i} className="bg-white border border-amber-100 rounded-lg p-2 text-xs">
                  <p className="text-[10px] text-amber-800">{n.autore} · {fmtDate(n.data)}</p>
                  <p className="text-zinc-900 mt-0.5">{n.testo}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* TIMELINE */}
        {p.storico_stati?.length > 0 && (
          <div>
            <p className="text-[10px] uppercase text-zinc-500 font-semibold mb-2">Storico stati</p>
            <div className="space-y-1">
              {p.storico_stati.map((s, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <CalIcon className="w-3 h-3 text-zinc-400" />
                  <span className="text-zinc-500">{fmtDate(s.data)}</span>
                  <span className="font-medium">{CONF(s.stato).label}</span>
                  <span className="text-zinc-400">— {s.autore}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end pt-2 border-t border-zinc-200 no-print">
          <Button variant="outline" onClick={stampaPDF} className="rounded-lg" data-testid="print-pratica">
            <Printer className="w-4 h-4 mr-2" /> Stampa / Esporta PDF
          </Button>
        </div>
      </div>
    </>
  );
}
